import React from "react";
import { BarChart, Bar, ComposedChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Sparkles } from "lucide-react";
import ForecastLayout, { useDrill } from "../../components/forecast/ForecastLayout";
import { KpiStrip, DriverPanel, LineButton, PageState, originOf } from "../../components/forecast/Lines";
import { Tile } from "../../components/Primitives";
import { useChartColors } from "../../lib/chartColors";
import { usePage, peso, pct0 } from "../../lib/forecastApi";
import { fmtDate } from "../../lib/origin.jsx";
import { cn } from "../../lib/utils";

const tick = { fill: "rgb(var(--muted))", fontSize: 10, fontFamily: "IBM Plex Mono" };
const item = (r) => <><div className="font-sans text-[12.5px] truncate max-w-[240px]">{r.sku_name}</div><div className="text-[10px] text-muted">{r.branch_name || r.sku_id}</div></>;
const pctCls = (p) => (p >= 0.8 ? "text-critical" : p >= 0.5 ? "text-warning" : "");

export const MODELS = {
  stockout: {
    title: "Stockout Risk Prediction",
    description: "SKU × branch lines ranked by how likely they are to run out and when — from on-hand, open POs and demand variability.",
    cols: [["Item", item], ["Class", r => r.class], ["14 days", r => <span className={pctCls(r.p14)}>{pct0(r.p14)}</span>, true],
      ["30 days", r => pct0(r.p30), true], ["Days of supply", r => r.days_of_supply, true], ["Lead time", r => `${r.lead_time_days} d`, true],
      ["Runs out ~", r => fmtDate(r.expected_stockout_date), true]],
    chart: { x: "horizon", y: "lines", fmt: v => v },
  },
  reorder: {
    title: "Reorder & Replenishment Prediction",
    description: "What to order, how much and by when — from the forecast, safety stock by ABC class and branch lead time.",
    cols: [["Item", item], ["Action", r => r.action], ["Qty", r => r.qty, true], ["Order value", r => peso(r.order_value_php), true],
      ["Order by", r => fmtDate(r.order_by), true], ["Supply / LT", r => `${r.days_of_supply ?? "—"} / ${r.lead_time_days} d`, true]],
    chart: { x: "branch", y: "value", fmt: peso },
  },
  aging: {
    title: "Aging & Slow-Stock Risk",
    description: "Stock likely to grow old or stop selling, with the capital it ties up and the action the Aging engine recommends.",
    cols: [["Item", item], ["Action", r => r.action], ["On hand", r => r.on_hand, true], ["Days of cover", r => Math.round(r.days_of_cover), true],
      ["Tied capital", r => peso(r.tied_capital_php), true], ["Sell-through 180 d", r => pct0(r.sell_through_180d), true]],
    chart: { x: "action", y: "value", fmt: peso },
  },
  weather: {
    title: "Weather-Driven Demand",
    description: "Heat-sensitive SKUs, how strongly heat (NASA POWER Cooling Degree Days) explains their demand and the change expected next month.",
    cols: [["SKU", item], ["Class", r => r.class], ["Heat R²", r => r.weather_r2?.toFixed(2), true],
      ["Next month", r => <span className={r.expected_change_next_month_pct < 0 ? "text-warning" : "text-success"}>{r.expected_change_next_month_pct > 0 ? "+" : ""}{r.expected_change_next_month_pct}%</span>, true],
      ["Annual value", r => peso(r.annual_value_php), true]],
    chart: { combo: true },
  },
};

function RankedList({ model, rows, origin }) {
  const cfg = MODELS[model];
  return (
    <Tile className="lg:col-span-7" dense testid={`fc-${model}-list`} title={`Ranked list · top ${rows.length}`}
          origin={originOf({ origin, label: cfg.title }, `${cfg.title} · ranked list`)}>
      <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 bg-surface z-10">
            <tr className="text-[10px] uppercase tracking-widest text-muted">
              {cfg.cols.map(([h, , num]) => <th key={h} className={cn("py-2 px-3 border-b border-border font-semibold whitespace-nowrap", num ? "text-right" : "text-left")}>{h}</th>)}
            </tr>
          </thead>
          <tbody className="font-mono-tabular">
            {rows.map(r => (
              <LineButton key={r.line} as="tr" line={{ id: r.line }} className="border-b border-border/60 last:border-b-0 table-row">
                {cfg.cols.map(([h, f, num]) => <td key={h} className={cn("py-2 px-3 whitespace-nowrap", num && "text-right")}>{f(r)}</td>)}
              </LineButton>
            ))}
          </tbody>
        </table>
        {!rows.length && <div className="p-6 text-sm text-muted" data-testid={`fc-${model}-empty`}>Nothing flagged in the latest engine cycle.</div>}
      </div>
    </Tile>
  );
}

function Chart({ model, chart, origin }) {
  const C = useChartColors();
  const { open } = useDrill();
  const cfg = MODELS[model].chart;
  return (
    <Tile testid={`fc-${model}-chart`} title={chart.title} origin={{ origin, source: chart.title }}
          action={<button data-testid={`fc-${model}-chart-explain`} onClick={() => open(chart.line)} className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"><Sparkles size={11} />Explain</button>}>
      <div style={{ height: 260 }}>
        <ResponsiveContainer>
          {cfg.combo ? (
            <ComposedChart data={chart.data} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}>
              <CartesianGrid stroke={C.border} strokeDasharray="2 4" />
              <XAxis dataKey="month" tick={tick} stroke={C.borderStrong} interval={3} />
              <YAxis yAxisId="u" tick={tick} stroke={C.borderStrong} tickFormatter={v => `${Math.round(v / 1000)}k`} />
              <YAxis yAxisId="c" orientation="right" tick={tick} stroke={C.borderStrong} />
              <Tooltip contentStyle={{ background: C.surface, border: `1px solid ${C.borderStrong}`, fontSize: 11 }} />
              <Bar yAxisId="u" dataKey="units" name="Units" fill={C.chart[0]} isAnimationActive={false} />
              <Line yAxisId="c" dataKey="avg_cdd" name="Avg CDD" stroke={C.chart[2]} strokeWidth={2} dot={false} isAnimationActive={false} />
            </ComposedChart>
          ) : (
            <BarChart data={chart.data} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey={cfg.x} tick={tick} stroke={C.borderStrong} interval={0} angle={chart.data.length > 5 ? -20 : 0} textAnchor={chart.data.length > 5 ? "end" : "middle"} height={chart.data.length > 5 ? 50 : 24} />
              <YAxis tick={tick} stroke={C.borderStrong} tickFormatter={cfg.fmt} width={56} />
              <Tooltip formatter={v => cfg.fmt(v)} contentStyle={{ background: C.surface, border: `1px solid ${C.borderStrong}`, fontSize: 11 }} />
              <Bar dataKey={cfg.y} fill={C.chart[0]} isAnimationActive={false} radius={[2, 2, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </Tile>
  );
}

// Stockout, reorder, aging and weather pages: indicators, a ranked list, a chart and the model's drivers — all drillable.
export default function ModelPage({ model }) {
  const { data, error, forbidden, reload } = usePage(model);
  const cfg = MODELS[model];
  return (
    <ForecastLayout title={cfg.title} description={cfg.description}>
      {!data ? <PageState error={error} forbidden={forbidden} onRetry={reload} /> : (
        <>
          <KpiStrip kpis={data.kpis} />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <RankedList model={model} rows={data.rows} origin={data.kpis[0].origin} />
            <div className="lg:col-span-5 space-y-6">
              <Chart model={model} chart={data.chart} origin={data.kpis[0].origin} />
              <DriverPanel testid={`fc-${model}-drivers`} title="Drivers" hint="Click a driver for its explanation and source rows"
                           drivers={data.drivers} centered={model === "weather"} />
            </div>
          </div>
        </>
      )}
    </ForecastLayout>
  );
}
